import './AthleteAddedToast.css';

import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export interface AthleteAddedToastPayload {
  name: string;
  number: string;
  position: string;
  returningstatus: string | null;
  imageThumbnailUrl: string;
}

export interface AthleteAddedToastProps {
  /** When false, exit animation runs; keep `payload` until `onExitComplete`. */
  open: boolean;
  payload: AthleteAddedToastPayload | null;
  onDismiss: () => void;
  onExitComplete: () => void;
}

const AthleteAddedToast: React.FC<AthleteAddedToastProps> = ({
  open,
  payload,
  onDismiss,
  onExitComplete,
}) => {
  const imageSrc = payload?.imageThumbnailUrl?.trim() ?? '';
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [showImageSkeleton, setShowImageSkeleton] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    setImageFailed(false);
    setShowImageSkeleton(false);
  }, [imageSrc]);

  useEffect(() => {
    if (!imageSrc || imageLoaded || imageFailed) {
      setShowImageSkeleton(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowImageSkeleton(true);
    }, 120);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [imageSrc, imageLoaded, imageFailed]);

  useEffect(() => {
    if (!imageSrc) return;

    let cancelled = false;
    const probe = new Image();

    const markLoaded = (): void => {
      if (!cancelled) setImageLoaded(true);
    };

    probe.onload = markLoaded;
    probe.onerror = () => {
      if (!cancelled) setImageFailed(true);
    };
    probe.src = imageSrc;

    if (probe.complete && probe.naturalWidth > 0) {
      markLoaded();
    } else if (typeof probe.decode === 'function') {
      void probe.decode().then(markLoaded).catch(() => {});
    }

    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [imageSrc]);

  const handleSurfaceAnimationEnd = useCallback(
    (e: React.AnimationEvent<HTMLDivElement>): void => {
      if (e.target !== e.currentTarget) return;
      if (e.animationName === 'athlete-added-toast-out') {
        onExitComplete();
      }
    },
    [onExitComplete],
  );

  const handleMediaImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>): void => {
    const img = e.currentTarget;
    if (typeof img.decode === 'function') {
      void img.decode().catch(() => {}).finally(() => {
        setImageLoaded(true);
      });
      return;
    }
    setImageLoaded(true);
  }, []);

  const handleMediaImageError = useCallback((): void => {
    setImageFailed(true);
  }, []);

  if (payload == null) {
    return null;
  }

  const initial = payload.name.trim().charAt(0).toUpperCase() || '?';

  return createPortal(
    <div
      className="athlete-added-toast-root"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-hidden={!open}
    >
      <div
        className={[
          'athlete-added-toast-surface',
          open ? '' : 'athlete-added-toast-surface--exit',
        ]
          .filter(Boolean)
          .join(' ')}
        onAnimationEnd={handleSurfaceAnimationEnd}
      >
        <button
          type="button"
          className="athlete-added-toast-close"
          onClick={onDismiss}
          aria-label="Close notification"
          disabled={!open}
        >
          ×
        </button>
        <div className="athlete-added-toast-media" aria-hidden="true">
          {imageSrc && !imageFailed ? (
            <>
              {showImageSkeleton ? (
                <div className="athlete-added-toast-media-skeleton" />
              ) : null}
              <img
                src={imageSrc}
                alt=""
                className={[
                  'athlete-added-toast-media-img',
                  imageLoaded ? 'athlete-added-toast-media-img--visible' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onLoad={handleMediaImageLoad}
                onError={handleMediaImageError}
                loading="eager"
                decoding="async"
              />
            </>
          ) : (
            <span className="athlete-added-toast-initial">{initial}</span>
          )}
        </div>
        <div className="athlete-added-toast-main">
          <p className="athlete-added-toast-headline">Athlete Added</p>
          <p className="athlete-added-toast-detail">
            {payload.name} · #{payload.number}
          </p>
          <div className="athlete-added-toast-tags">
            {payload.returningstatus ? (
              <span className="athlete-added-toast-tag">
                {payload.returningstatus}
              </span>
            ) : null}
            <span className="athlete-added-toast-tag">{payload.position}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AthleteAddedToast;
